import isPlainObject from 'lodash/isPlainObject';
import isFunction from 'lodash/isFunction';
import { isDevelopment } from '@v4/environment';
import { getLocale } from '@v4/common/utils/locale';
import { getCurrencyCode } from '@v4/common/utils/currency';

export const LIST_HOME_PAGE = 'home-page';
export const LIST_COMPARE_PAGE = 'compare-page';
export const LIST_VDP_PAGE = 'vdp';
export const LIST_VIP_PAGE = 'vip';
export const LIST_SINGLE_GUIDE_PAGE = 'single-guide';

export const ListEnum = Object.freeze({
    LIST_HOME_PAGE,
    LIST_COMPARE_PAGE,
    LIST_VDP_PAGE,
    LIST_VIP_PAGE,
    LIST_VOP_PAGE: 'vop',
    LIST_VLP_PAGE: 'versions_list_page',
    LIST_SINGLE_GUIDE_PAGE,
});

/**
 * Checks if it's a valid product
 *
 * @param {object} product - product item
 * @param {object} actionField - actions
 */
export const isValidProduct = (product) => {
    if (!isPlainObject(product) || (!product.name && !product.id)) {
        return false;
    }

    return true;
};

/**
 * Checks if `actionField.list` property is set
 *
 * @param {object} actionField - actions
 * @returns {boolean}
 */
export const hasActionFieldList = (actionField) => isPlainObject(actionField) && actionField.list;

/**
 * Checks if an array it's empty or not
 *
 * @param {array} list - list of items
 * @returns {boolean}
 */
export const isEmptyList = (list) => !(list && Array.isArray(list) && list.length);

/**
 * Returns an object with key `eventCallback` and value the cb function
 * when exists otherwise false.
 *
 * @param {requestCallback} cb - callback function
 * @returns {object|false}
 */
export const getEventCallback = (cb) => isFunction(cb) && { eventCallback: cb };

/**
 * Returns an array of valid product items
 *
 * @param {object|array<object>} product - product item or list of items
 * @param {object} actionField - actions
 * @returns {array<object>} valid products
 */
export const retrieveValidProducts = (product, actionField) => {
    const products = [];

    const isValid = (product, actionField) => isValidProduct(product) && hasActionFieldList(actionField);

    if (Array.isArray(product)) {
        product.forEach((item) => {
            if (isValid(item, actionField)) {
                products.push(item);
            }
        });
    } else if (isValid(product, actionField)) {
        products.push(product);
    }

    return products;
};

/**
 * Push data with dataLayer
 *
 * @see {@link https://developers.google.com/analytics/devguides/collection/analyticsjs/enhanced-ecommerce}
 * @see {@link https://developers.google.com/tag-manager/enhanced-ecommerce}
 * @param  {...any} args
 */
export const push = (...args) => {
    if (
        !isDevelopment() &&
        window.ecDataLayerEnabled &&
        window.dataLayer &&
        isFunction(window.dataLayer.push) &&
        args.length
    ) {
        window.dataLayer.push(...args);
    }
};

/**
 * Measures product impressions and also tracks a standard
 * pageview for the tag configuration.
 *
 * @see {@link https://developers.google.com/tag-manager/enhanced-ecommerce#product-impressions}
 * @param {array<object>} impressionList - impressions
 */
export const pushImpressions = (impressionList) => {
    let impressions = impressionList;

    if (isEmptyList(impressions)) {
        return;
    }

    impressions = impressions.filter(
        (impression) => isValidProduct(impression) && hasActionFieldList({ list: impression.list })
    );

    push({
        ecommerce: {
            currencyCode: getCurrencyCode(getLocale()),
            impressions,
        },
    });
};

/**
 * Measuring Promotions
 *
 * @see {@link https://developers.google.com/tag-manager/enhanced-ecommerce#promo}
 * @param {array<object>} promotionList - promotions
 */
export const pushPromoView = (promotionList) => {
    let promotions = promotionList;

    if (isEmptyList(promotions)) {
        return;
    }

    promotions = promotions.filter((promotion) => isValidProduct(promotion));

    push({
        ecommerce: {
            promoView: {
                promotions,
            },
        },
    });
};

/**
 * Measuring Views of Product Details
 *
 * @see {@link https://developers.google.com/tag-manager/enhanced-ecommerce#details}
 * @param {object|array<object>} product - product item or list of items
 * @param {object} actionField - actions
 */
export const pushViewsProductDetails = (product, actionField = null) => {
    const products = retrieveValidProducts(product, actionField);

    if (!products.length) {
        return;
    }

    push({
        ecommerce: {
            detail: {
                actionField,
                products,
            },
        },
    });
};

/**
 * Measuring Product Clicks
 *
 * @see {@link https://developers.google.com/tag-manager/enhanced-ecommerce#product-clicks}
 * @param {object|array<object>} product - product item or list of items
 * @param {object} actionField - actions
 * @param {requestCallback} cb - handle navigation after the ecommerce data is sent to Google Analytics
 */
export const pushProductClick = (product, actionField = null, cb) => {
    const products = retrieveValidProducts(product, actionField);

    if (!products.length) {
        return;
    }

    push({
        event: 'productClick',
        ecommerce: {
            click: {
                actionField,
                products,
            },
        },
        ...getEventCallback(cb),
    });
};

/**
 * Measuring Promotion Clicks
 *
 * @see {@link https://developers.google.com/tag-manager/enhanced-ecommerce#promo-clicks}
 * @param {object} product - promotion item
 * @param {requestCallback} cb - handle navigation after the ecommerce data is sent to Google Analytics
 */
export const pushPromotionClick = (product = {}, cb) => {
    if (!isValidProduct(product)) {
        return;
    }

    push({
        event: 'promotionClick',
        ecommerce: {
            promoClick: {
                promotions: [product],
            },
        },
        ...getEventCallback(cb),
    });
};
